import { memo, useCallback } from "react";

import { isNonEmptyString } from "@/utils/string";

import { useFormField } from "../../../hooks/useFormField";
import type { FieldRenderInputProps, FieldValue } from "../../../types";
import {
  extractValueFromValueLabel,
  getSelectedOptionLabel,
} from "../../../utils/value-label-utils";
import type { BaseFieldProps } from "../BaseField";
import { BaseField } from "../BaseField";

import { DynamicSelectFieldPresentation } from "./DynamicSelectField.presenter";
import { useDynamicSelectField } from "./useDynamicSelectField";
import type { FieldCode } from "./useFieldDependencyResolve";

interface DynamicSingleSelectFieldContainerProps extends BaseFieldProps {
  /** 依存元フィールドのコード */
  sourceFieldCode: FieldCode;
  /** 依存先フィールドのコード */
  targetFieldCode: FieldCode;
  /** フォーム内のすべてのフィールド値（依存元の値を取得するため） */
  allValues?: FieldValue[];
}

/**
 * 動的セレクトフィールドのコンテナ層
 * 責務: SingleSelect専用のビジネスロジックとUI層の統合
 */
export const DynamicSingleSelectFieldContainer = memo(
  ({
    fieldId,
    field,
    disabled,
    readOnly,
    className,
    sourceFieldCode,
    targetFieldCode,
    allValues = [],
    layout,
    "data-testid": testId,
  }: DynamicSingleSelectFieldContainerProps) => {
    const { options, uiState, hasSourceValueChanged } = useDynamicSelectField({
      fieldId,
      field,
      allValues,
      sourceFieldCode,
      targetFieldCode,
    });

    const { handleChange, value } = useFormField({
      fieldId,
      attribute: field,
    });

    // 依存元フィールドが変更されている場合は、値をundefinedに更新する
    if (value.currentValue && hasSourceValueChanged) {
      handleChange(undefined);
    }

    const renderInput = useCallback(
      ({
        value: inputValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        error: fieldError,
        placeholder,
        disabled: isDisabled,
        readOnly: isReadOnly,
        "data-testid": inputTestId,
      }: FieldRenderInputProps) => {
        if (!field) {
          return <div></div>;
        }

        // SingleSelect用のロジック
        const selectValue = extractValueFromValueLabel(inputValue);

        const hasValue = isNonEmptyString(selectValue);
        // 任意項目（required=false）の場合のみクリア可能
        const handleClear = !field.required
          ? () => handleChange(undefined)
          : undefined;

        const handleValueChange = (newValue: string) => {
          if (newValue && newValue !== "") {
            const selectedOption = options.find(
              (opt) => opt.value === newValue,
            );
            if (selectedOption?.label) {
              handleChange({
                value: newValue,
                label: selectedOption.label,
                ...(selectedOption.description && {
                  description: selectedOption.description,
                }),
              });
            } else {
              handleChange(newValue);
            }
          } else {
            handleChange(undefined);
          }
        };

        return (
          <DynamicSelectFieldPresentation
            uiState={uiState}
            options={options}
            value={selectValue}
            onChange={handleValueChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={isDisabled}
            readOnly={isReadOnly}
            error={fieldError}
            placeholder={placeholder}
            testId={inputTestId}
            getSelectedOptionLabel={(value) =>
              getSelectedOptionLabel(value, options)
            }
            hasValue={hasValue}
            onClear={handleClear}
          />
        );
      },
      [field, options, uiState],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        disabled={disabled}
        readOnly={readOnly}
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

DynamicSingleSelectFieldContainer.displayName =
  "DynamicSingleSelectFieldContainer";
